import { useCallback, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Footer, Header, Hero } from '../components/extractor/Chrome';
import UploadZone from '../components/extractor/UploadZone';
import Controls from '../components/extractor/Controls';
import FileQueue, { QueueToolbar } from '../components/extractor/FileQueue';
import CropDialog from '../components/extractor/CropDialog';
import ResultsPanel from '../components/extractor/ResultsPanel';
import {
  FaqSection,
  HowItWorks,
  LanguagesAndLimits,
  PrivacySection,
} from '../components/extractor/InfoSections';
import { useExtractor } from '../hooks/useExtractor';

export default function TextExtractor() {
  const x = useExtractor();
  const [cropId, setCropId] = useState(null);
  const cropItem = x.items.find((i) => i.id === cropId) || null;

  const retryOtherMode = useCallback(
    async (id) => {
      x.selectMode(x.mode === 'document' ? 'poster' : 'document');
      setTimeout(() => x.extractOne(id), 0);
    },
    [x],
  );

  const retryOtherEngine = useCallback(
    async (id) => {
      x.selectEngine(x.activeEngine === 'classic' ? 'nextgen' : 'classic');
      setTimeout(() => x.extractOne(id), 0);
    },
    [x],
  );

  return (
    <div className="min-h-screen">
      <Header />
      <div className="page-aura">
        <main className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
          <Hero />

          <div aria-atomic="true" aria-live="polite" className="sr-only" data-testid="sr-announcer" role="status">
            {x.announcement}
          </div>

          <div className="mt-10 space-y-8 sm:space-y-10">
            <UploadZone onFiles={x.addFiles} />

            {x.validationMessages.length ? (
              <ul className="space-y-2" data-testid="validation-messages">
                {x.validationMessages.map((m, i) => (
                  <li
                    className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800"
                    key={`${i}-${m.text}`}
                  >
                    <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    {m.text}
                  </li>
                ))}
              </ul>
            ) : null}

            <Controls
              activeEngine={x.activeEngine}
              disabled={x.isRunning}
              languageId={x.languageId}
              mode={x.mode}
              nextGenBlocker={x.nextGenBlocker}
              nextGenHidden={x.nextGenHidden}
              nextGenReady={x.nextGenReady}
              onEngine={x.selectEngine}
              onLanguage={x.setLanguageId}
              onMode={x.selectMode}
              requestedEngine={x.requestedEngine}
              routing={x.routing}
            />

            <FileQueue
              canUseNextGen={!x.nextGenHidden && x.languageId === 'english'}
              isRunning={x.isRunning}
              items={x.items}
              mode={x.mode}
              onCrop={setCropId}
              onExtractOne={x.extractOne}
              onRemove={x.removeItem}
              onRetryOtherEngine={retryOtherEngine}
              onRetryOtherMode={retryOtherMode}
              onRetryWithClassic={x.retryWithClassic}
              onRotate={x.rotateItem}
            />

            <QueueToolbar
              isRunning={x.isRunning}
              items={x.items}
              onCancel={x.cancel}
              onClearAll={x.clearAll}
              onExtractAll={x.extractAll}
            />

            <ResultsPanel copyAllText={x.copyAllText} items={x.successful} onTextChange={x.updateText} />

            <HowItWorks />
            <LanguagesAndLimits />
            <PrivacySection />
            <FaqSection />
          </div>
        </main>
      </div>
      <Footer />

      <CropDialog
        item={cropItem}
        onApply={(rect) => (cropItem ? x.setCrop(cropItem.id, rect) : null)}
        onClose={() => setCropId(null)}
        open={Boolean(cropItem)}
      />
    </div>
  );
}
